// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import OrderDetailsShipmentInfoDetails from "@eisinc/App/Components/OrderDetails/OrderDetailsShipmentInfoDetails";
import { OrderDetailsTableStyle } from "@eisinc/App/SharedStyles/OrderDetailsStyles";
import { getSettingsCollection } from "@eisinc/App/Store/Selectors/SettingsSelectors";
import DataTable, { DataTableProps } from "@eisinc/MobiusOverrides/DataTable/DataTable";
import DataTableBody from "@eisinc/MobiusOverrides/DataTable/DataTableBody";
import DataTableCell from "@eisinc/MobiusOverrides/DataTable/DataTableCell";
import DataTableHead, { DataTableHeadProps } from "@eisinc/MobiusOverrides/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@eisinc/MobiusOverrides/DataTable/DataTableHeader";
import DataTableRow, { DataTableRowProps } from "@eisinc/MobiusOverrides/DataTable/DataTableRow";
import GridContainer, { GridContainerProps } from "@eisinc/MobiusOverrides/GridContainer/GridContainer";
import {
    constants,
    headerColumns,
    numberOfColumn,
    ShipmentPackageData,
    ShipmentPackageDataStyles,
    ShipmentTrackingStatus,
    ShipmentTrackingUrls,
    ShipmentTrackingUrlsStyles,
} from "@eisinc/Overrides/Widgets/OrderDetails/OrderDetailsShipmentPackages";
import ChevronDown from "@eisinc/XDL/Icons/ChevronDown";
import ChevronUp from "@eisinc/XDL/Icons/ChevronUp";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LocalizedDateTime from "@insite/content-library/Components/LocalizedDateTime";
import { DataTableCellBaseProps } from "@insite/mobius/DataTable/DataTableCellBase";
import GridItem from "@insite/mobius/GridItem";
import Hidden from "@insite/mobius/Hidden";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useMemo, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    order: state.pages.orderStatus.order,
    // XNG-Changes
    hideInvoiceInShipments:
        (getSettingsCollection(state).customSettings as any)?.hideInvoiceInShipments?.toLowerCase() === "true",
});

type Props = WidgetProps & ReturnType<typeof mapStateToProps>;

export interface OrderStatusShipmentPackagesStyles {
    dataTable?: DataTableProps;
    tableHead?: DataTableHeadProps;
    shipmentDateHeader?: DataTableHeaderProps;
    carrierHeader?: DataTableHeaderProps;
    shipViaHeader?: DataTableHeaderProps;
    trackingNumberHeader?: DataTableHeaderProps;
    tableRow?: DataTableRowProps;
    shipDateCell?: DataTableCellBaseProps;
    carrierCell?: DataTableCellBaseProps;
    shipViaCell?: DataTableCellBaseProps;
    trackingNumberCell?: DataTableCellBaseProps;
    // XNG-Changes
    tableHeadStyle?: DataTableHeadProps;
    tableHeader?: DataTableHeaderProps;
    tableRowStyle?: DataTableRowProps;
    tableCellGridContainerStyle?: GridContainerProps;
    trackingNumberCellWrapper?: InjectableCss;
    urlTrackingStyle?: LinkPresentationProps;
    shipmentPackageDataStyles?: ShipmentPackageDataStyles;
    shipmentTrackingUrlsStyles?: ShipmentTrackingUrlsStyles;
}

export const orderStatusShipmentPackagesStyles: OrderStatusShipmentPackagesStyles = {};

const styles = orderStatusShipmentPackagesStyles;

const OrderStatusShipmentPackages: FC<Props> = ({ order, hideInvoiceInShipments }) => {
    // <XNG-Changes>
    const styles = mergeToNew(OrderDetailsTableStyle, orderStatusShipmentPackagesStyles);

    const [showAccordionById, setShowAccordionById] = useState({} as { [key: string]: boolean });

    const toggleAccordion = (id: string) => setShowAccordionById(prev => ({ ...prev, [id]: !prev[id] }));

    const shipmentPackagesById = useMemo(
        () => Object.fromEntries((order?.shipmentPackages ?? []).map(sp => [sp.id, sp])),
        [order?.shipmentPackages],
    );

    const orderLinesById = useMemo(
        () => Object.fromEntries((order?.orderLines ?? []).map(ol => [ol.id, ol])),
        [order?.orderLines],
    );
    // </XNG-Changes>

    if (!order || !order.shipmentPackages || order.shipmentPackages.length === 0) {
        return null;
    }

    // XNG-Changes: change structure
    return (
        <StyledWrapper {...styles.tableWrapper}>
            <Typography {...styles.infoLabel}>{translate("Shipping Information")}</Typography>
            <DataTable>
                <DataTableHead {...styles.tableHeadStyle}>
                    {headerColumns.map((column, index) => {
                        if (hideInvoiceInShipments && index === 5) return null;
                        return (
                            <DataTableHeader
                                key={`${column}_${index}`}
                                {...styles.tableHeader}
                                className={index === headerColumns.length - 1 ? "hide-print" : ""}
                            >
                                {translate(column)}
                            </DataTableHeader>
                        );
                    })}
                </DataTableHead>
                <DataTableBody {...styles.tableBodyStyle}>
                    {order.shipmentPackages.map(shipmentPackage => {
                        return (
                            <React.Fragment key={shipmentPackage.id}>
                                <DataTableRow
                                    {...styles.tableRowStyle}
                                    key={shipmentPackage.id}
                                    className={showAccordionById[shipmentPackage.id] ? "accordion-color" : ""}
                                >
                                    <DataTableCell {...styles.tableCellStyle}>
                                        <GridContainer gap={0} {...styles.tableCellGridContainerStyle}>
                                            <Hidden {...styles.hiddenHeaderStyle}>
                                                <GridItem {...styles.tableCellGridStyle}>
                                                    {translate(headerColumns[0])}
                                                </GridItem>
                                            </Hidden>
                                            <GridItem {...styles.tableCellGridStyle}>
                                                <LocalizedDateTime
                                                    dateTime={new Date(shipmentPackage.shipmentDate)}
                                                    options={{
                                                        year: "numeric",
                                                        month: "numeric",
                                                        day: "numeric",
                                                        timeZone: "UTC",
                                                    }}
                                                />
                                            </GridItem>
                                        </GridContainer>
                                    </DataTableCell>
                                    <DataTableCell {...styles.tableCellStyle}>
                                        <GridContainer gap={0} {...styles.tableCellGridContainerStyle}>
                                            <Hidden {...styles.hiddenHeaderStyle}>
                                                <GridItem {...styles.tableCellGridStyle}>
                                                    {translate(headerColumns[1])}
                                                </GridItem>
                                            </Hidden>
                                            <GridItem {...styles.tableCellGridStyle}>
                                                {shipmentPackage.carrier || "– –"}
                                            </GridItem>
                                        </GridContainer>
                                    </DataTableCell>
                                    <DataTableCell {...styles.tableCellStyle}>
                                        <GridContainer gap={0} {...styles.tableCellGridContainerStyle}>
                                            <Hidden {...styles.hiddenHeaderStyle}>
                                                <GridItem {...styles.tableCellGridStyle}>
                                                    {translate(headerColumns[2])}
                                                </GridItem>
                                            </Hidden>
                                            <GridItem {...styles.tableCellGridStyle}>
                                                {shipmentPackage.shipVia || "– –"}
                                            </GridItem>
                                        </GridContainer>
                                    </DataTableCell>
                                    <DataTableCell {...styles.tableCellStyle}>
                                        <GridContainer gap={0} {...styles.tableCellGridContainerStyle}>
                                            <Hidden {...styles.hiddenHeaderStyle}>
                                                <GridItem {...styles.tableCellGridStyle}>
                                                    {translate(headerColumns[3])}
                                                </GridItem>
                                            </Hidden>
                                            <GridItem {...styles.tableCellGridStyle}>
                                                <StyledWrapper {...styles.trackingNumberCellWrapper}>
                                                    <ShipmentTrackingUrls
                                                        shipmentPackageId={shipmentPackage.id}
                                                        order={order}
                                                    />
                                                </StyledWrapper>
                                            </GridItem>
                                        </GridContainer>
                                    </DataTableCell>
                                    <DataTableCell {...styles.tableCellStyle}>
                                        <GridContainer gap={0} {...styles.tableCellGridContainerStyle}>
                                            <Hidden {...styles.hiddenHeaderStyle}>
                                                <GridItem {...styles.tableCellGridStyle}>
                                                    {translate(headerColumns[4])}
                                                </GridItem>
                                            </Hidden>
                                            <GridItem {...styles.tableCellGridStyle}>
                                                <StyledWrapper {...styles.trackingNumberCellWrapper}>
                                                    <ShipmentTrackingStatus
                                                        shipmentPackageId={shipmentPackage.id}
                                                        order={order}
                                                    />
                                                </StyledWrapper>
                                            </GridItem>
                                        </GridContainer>
                                    </DataTableCell>

                                    {!hideInvoiceInShipments && (
                                        <DataTableCell {...styles.tableCellStyle}>
                                            <GridContainer gap={0} {...styles.tableCellGridContainerStyle}>
                                                <Hidden {...styles.hiddenHeaderStyle}>
                                                    <GridItem {...styles.tableCellGridStyle}>
                                                        {translate(headerColumns[5])}
                                                    </GridItem>
                                                </Hidden>
                                                <GridItem {...styles.tableCellGridStyle}>
                                                    <ShipmentPackageData
                                                        id={shipmentPackage.id}
                                                        task={constants.invoiceNumber}
                                                        order={order}
                                                    />
                                                </GridItem>
                                            </GridContainer>
                                        </DataTableCell>
                                    )}
                                    <DataTableCell
                                        {...styles.tableCellStyle}
                                        className={`hide-print ${
                                            showAccordionById[shipmentPackage.id] ? "hide-on-mobile" : ""
                                        }`}
                                    >
                                        <GridItem {...styles.showAccordionGridStyle}>
                                            {(shipmentPackagesById[shipmentPackage.id]?.shipmentPackageLineDtos
                                                ?.length ?? 0) > 0 && (
                                                <Link
                                                    {...styles.shippingDetailsStyle}
                                                    icon={{
                                                        iconProps: {
                                                            src: showAccordionById[shipmentPackage.id]
                                                                ? ChevronUp
                                                                : ChevronDown,
                                                            size: 13,
                                                            color: "primary.main",
                                                            css: css`
                                                                line-height: 0;
                                                                margin-right: 5px;
                                                                vertical-align: middle;
                                                            `,
                                                        },
                                                        position: "right",
                                                    }}
                                                    onClick={() => toggleAccordion(shipmentPackage.id)}
                                                >
                                                    {translate("Shipping Details")}
                                                </Link>
                                            )}
                                        </GridItem>
                                    </DataTableCell>
                                </DataTableRow>
                                {showAccordionById[shipmentPackage.id] && (
                                    <DataTableRow
                                        key={`shipping-package-${shipmentPackage.id}`}
                                        {...styles.accordionRowStyle}
                                    >
                                        <DataTableCell colSpan={numberOfColumn} {...styles.accordionCellStyle}>
                                            <OrderDetailsShipmentInfoDetails
                                                orderLines={Object.values(orderLinesById)}
                                                orderLineShipments={
                                                    shipmentPackagesById[shipmentPackage.id]?.shipmentPackageLineDtos ??
                                                    []
                                                }
                                            />
                                            <Link
                                                {...styles.shippingDetailsStyle}
                                                icon={{
                                                    iconProps: {
                                                        src: showAccordionById[shipmentPackage.id]
                                                            ? ChevronUp
                                                            : ChevronDown,
                                                        size: 13,
                                                        color: "primary.main",
                                                        css: css`
                                                            line-height: 0;
                                                            margin-right: 5px;
                                                            vertical-align: middle;
                                                        `,
                                                    },
                                                    position: "right",
                                                }}
                                                className="hide-on-desktop"
                                                onClick={() => toggleAccordion(shipmentPackage.id)}
                                            >
                                                {translate("Shipping Details")}
                                            </Link>
                                        </DataTableCell>
                                    </DataTableRow>
                                )}
                            </React.Fragment>
                        );
                    })}
                </DataTableBody>
            </DataTable>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(OrderStatusShipmentPackages),
    definition: {
        allowedContexts: ["OrderStatusPage"],
        displayName: "Shipment Packages",
        group: "Order Status",
    },
};

export default widgetModule;
